import {
  PointerAction,
  HoverDetectionService,
  NodeResizeService,
  SelectionService
} from '@/modules/map-editor';
import type {
  PointerState,
  HandleMoveParams,
  HandleDownParams,
  HandleUpParams,
  MovedNodeSnapshot,
  MultiDragOffset
} from '@/modules/map-editor';
import type { Node, Edge } from '@/modules/map-editor';
import { Scale } from '@/services/canvas/Scale';
import { EDITING_ELEM_TYPES } from '@/models/EDITING_ELEM_TYPES';

/**
 * Service for handling pointer interactions on the map canvas.
 *
 * Manages hover detection, node dragging, and map panning. Delegates
 * node-resize math to {@link NodeResizeService} and element selection
 * orchestration to {@link SelectionService}.
 */
export class PointerInteractionService {
  private readonly state: PointerState;
  private hoverDetection: HoverDetectionService;
  private nodeResizeService: NodeResizeService;
  private selectionService: SelectionService;

  // Mouse tracking for drag calculations
  private mouse = {
    x: 0,
    y: 0,
    initialDragX: 0,
    initialDragY: 0,
    storedDragX: 0,
    storedDragY: 0,
    movedNode: null as MovedNodeSnapshot | null
  };

  private nodeDX = 0;
  private nodeDY = 0;
  private nodeWasDragged = false;

  // Multi-selection drag state
  private multiDragOffsets: MultiDragOffset[] = [];
  private multiDragInitialPositions: MovedNodeSnapshot[] = [];
  private multiDragWasDragged = false;

  constructor(
    initialState: PointerState,
    nodes: Node[],
    edges: Edge[]
  ) {
    this.state = initialState;
    this.hoverDetection = new HoverDetectionService(nodes, edges);
    this.nodeResizeService = new NodeResizeService(initialState);
    this.selectionService = new SelectionService(initialState);
  }

  /**
   * Handle pointer move event.
   *
   * @param params - Move interaction parameters
   */
  handleMove(params: HandleMoveParams): void {
    const {
      ctx,
      position,
      zoomLevel,
      isShiftPressed = false,
      canvasWidth,
      canvasHeight,
      nodes,
      edges,
      mapAreaService,
      sendPointerState,
      userCurrent,
      selectedNode: activeNode,
      updateNodeDesign: nodeDesignUpdate
    } = params;

    const { x, y } = position;
    const movementX = x - this.mouse.initialDragX;
    const movementY = y - this.mouse.initialDragY;

    this.mouse.x = x;
    this.mouse.y = y;

    const isMovement = this.mouse.x - this.mouse.storedDragX !== 0 ||
      this.mouse.y - this.mouse.storedDragY !== 0;

    // Detect hover if not dragging
    if (!this.state.isMouseDown) {
      const hoverResult = this.hoverDetection.detectHover(ctx, position, zoomLevel);

      this.hoverDetection.updateHoverState(hoverResult);

      this.state.hoveredNodeId = hoverResult.hoveredNodeId;
      this.state.hoveredEdgeId = hoverResult.hoveredEdgeId;

      if (activeNode && !this.state.isNodeSizing) {
        const sizerPointHovered = activeNode.checkSizerPointHovered({
          x: position.x,
          y: position.y
        });

        if (sizerPointHovered) {
          // Set the selected SizerPoint from the active node
          this.state.hoveredSizerPoint = activeNode.selectedPoint;
        } else if (this.state.hoveredSizerPoint) {
          // Reset if the cursor moves away from the SizerPoint
          this.state.hoveredSizerPoint = null;
        }
      } else {
        // If there is no active node, or we are in resize mode - reset
        this.state.hoveredSizerPoint = null;
      }

      // Multi-selection bounding box hover detection
      const wasHovering = this.state.isHoveringSelectionBox;
      this.state.isHoveringSelectionBox = this.isInsideSelectionBox(
        x, y, canvasWidth, canvasHeight, zoomLevel, params.selectedNodes, mapAreaService
      );

      // Trigger re-render when hover state changes for visual feedback
      if (this.state.isHoveringSelectionBox !== wasHovering) {
        mapAreaService.renderFrame();
      }
    }

    // Handle node resizing — delegated to NodeResizeService
    if (this.state.isNodeSizing && this.state.hoveredSizerPoint) {
      if (activeNode) {
        this.nodeResizeService.performResize(
          activeNode,
          movementX,
          movementY,
          isShiftPressed ?? false,
          window.devicePixelRatio,
          nodeDesignUpdate
        );
      }

      this.mouse.initialDragX = x;
      this.mouse.initialDragY = y;
      return;
    }

    // Handle multi-selection dragging
    if (
      this.state.action === PointerAction.DRAG_MULTI_SELECTION &&
      this.multiDragOffsets.length > 0 &&
      this.state.isMouseDown
    ) {
      if (!this.multiDragWasDragged && isMovement) {
        this.multiDragWasDragged = true;
      }

      const mapArea = mapAreaService.getState();
      mapAreaService.updateMap(true);

      for (const offset of this.multiDragOffsets) {
        const node = nodes.find(n => n.id === offset.nodeId);
        if (!node) continue;

        node.x = Scale.xInv(x, canvasWidth / 2, zoomLevel) - offset.offsetX - mapArea.x;
        node.y = Scale.yInv(y, canvasHeight / 2, zoomLevel) - offset.offsetY - mapArea.y;

        // Update connected edge positions
        if (node.edgesFrom && node.edgesFrom.length) {
          node.edgesFrom.forEach((edgeId: number) => {
            const edge = edges.find(e => e.id === edgeId);
            if (edge) {
              edge.startPosX = node.x;
              edge.startPosY = node.y;
              mapAreaService.updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
            }
          });
        }

        if (node.edgesTo && node.edgesTo.length) {
          node.edgesTo.forEach((edgeId: number) => {
            const edge = edges.find(e => e.id === edgeId);
            if (edge) {
              edge.endPosX = node.x;
              edge.endPosY = node.y;
              mapAreaService.updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
            }
          });
        }
      }

      return;
    }

    // Handle node dragging
    if (
      this.state.action === PointerAction.DRAG_NODE &&
      this.state.hoveredNodeId !== null &&
      this.state.isMouseDown
    ) {
      const movedNode = nodes.find(n => n.id === this.state.hoveredNodeId);
      if (!movedNode) return;

      // Track if node was actually dragged
      if (!this.nodeWasDragged && isMovement) {
        this.mouse.movedNode = {
          id: movedNode.id,
          x: movedNode.x,
          y: movedNode.y
        };
        this.nodeWasDragged = true;
      }

      // Continuous rendering during drag
      mapAreaService.updateMap(true);

      // Calculate new position using Scale
      const mapArea = mapAreaService.getState();
      movedNode.x = Scale.xInv(x, canvasWidth / 2, zoomLevel) - this.nodeDX - mapArea.x;
      movedNode.y = Scale.yInv(y, canvasHeight / 2, zoomLevel) - this.nodeDY - mapArea.y;

      // Update connected edge positions — animation loop rebuilds edge caches via updateElement
      if (movedNode.edgesFrom && movedNode.edgesFrom.length) {
        movedNode.edgesFrom.forEach((edgeId: number) => {
          const edge = edges.find(e => e.id === edgeId);
          if (edge) {
            edge.startPosX = movedNode.x;
            edge.startPosY = movedNode.y;
            mapAreaService.updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
          }
        });
      }

      if (movedNode.edgesTo && movedNode.edgesTo.length) {
        movedNode.edgesTo.forEach((edgeId: number) => {
          const edge = edges.find(e => e.id === edgeId);
          if (edge) {
            edge.endPosX = movedNode.x;
            edge.endPosY = movedNode.y;
            mapAreaService.updateElement(true, edgeId, EDITING_ELEM_TYPES.EDGE);
          }
        });
      }

      // No cache rebuild needed for the node — only position changed.
      // The animation loop (updateMap) redraws from existing cache at new position.

      return;
    }

    // Handle lasso drag selection
    if (this.state.action === PointerAction.DRAG_SELECTION && this.state.isMouseDown && this.state.selectionBox) {
      this.state.selectionBox.endX = x;
      this.state.selectionBox.endY = y;
      mapAreaService.renderFrame();
      this.mouse.initialDragX = x;
      this.mouse.initialDragY = y;
      return;
    }

    // Handle map dragging
    if (this.state.action === PointerAction.DRAG_MAP && this.state.isMouseDown) {
      mapAreaService.movement = true;

      // Update map area coordinates
      const mapArea = mapAreaService.getState();
      mapArea.x += movementX / zoomLevel;
      mapArea.y += movementY / zoomLevel;

      mapAreaService.updateMap(true);

      this.mouse.initialDragX = x;
      this.mouse.initialDragY = y;
      return;
    }

    // Send pointer state for collaboration (if callback provided)
    if (!this.state.isMouseDown && userCurrent) {
      const pointerState = {
        editor: {
          x: Scale.xInv(x, canvasWidth / 2, zoomLevel) - mapAreaService.x,
          y: Scale.yInv(y, canvasHeight / 2, zoomLevel) - mapAreaService.y
        },
        user: userCurrent
      };
      sendPointerState(pointerState);
    }

    // Save current position for next step
    this.mouse.initialDragX = x;
    this.mouse.initialDragY = y;
  }

  /**
   * Handle pointer down event.
   *
   * @param params - Down interaction parameters
   */
  handleDown(params: HandleDownParams): void {
    const {
      ctx,
      position,
      zoomLevel,
      canvasWidth,
      canvasHeight,
      nodes,
      mapAreaService,
      hasPermission,
      selectedNode: activeNode,
      nodeSizerPointHovered,
      saveNodePosition
    } = params;
    // TODO: restore read-only guard (isReadOnly from params) when permission system is finalized

    this.nodeDX = 0;
    this.nodeDY = 0;
    this.nodeWasDragged = false;
    this.mouse.movedNode = null;
    this.state.isNodeSizing = false;

    // Preserve hover state before reset — used for multi-drag initiation
    const wasHoveringSelectionBox = this.state.isHoveringSelectionBox;
    this.state.isHoveringSelectionBox = false;

    this.state.isMouseDown = true;
    mapAreaService.updateElement(true);

    const { x, y } = position;
    this.mouse.x = this.mouse.storedDragX = x;
    this.mouse.y = this.mouse.storedDragY = y;
    this.mouse.initialDragX = x;
    this.mouse.initialDragY = y;

    // Spacebar held → hand tool: pan the map regardless of what is under the
    // cursor (skip hit-testing, node drag, resize and selection entirely).
    if (this.state.isSpacePressed) {
      this.state.hoveredNodeId = null;
      this.state.hoveredEdgeId = null;
      this.state.hoveredSizerPoint = null;
      this.state.action = PointerAction.DRAG_MAP;
      return;
    }

    this.state.hoveredNodeId = null;
    this.state.hoveredEdgeId = null;

    // Detect what was clicked
    const hoverResult = this.hoverDetection.detectHover(ctx, position, zoomLevel);

    this.hoverDetection.updateHoverState(hoverResult);

    this.state.hoveredNodeId = hoverResult.hoveredNodeId;
    this.state.hoveredEdgeId = hoverResult.hoveredEdgeId;

    // Check for node sizing mode — delegated to NodeResizeService
    if (activeNode && nodeSizerPointHovered) {
      this.state.isNodeSizing = true;
      this.state.hoveredSizerPoint = nodeSizerPointHovered;

      // Calculate node dimensions ratio for resize
      this.nodeResizeService.startResize(activeNode.width / activeNode.height || 1);

      this.state.action = PointerAction.RESIZE_NODE;
      return;
    }

    this.state.hoveredSizerPoint = null;

    // Multi-selection drag initiation — click inside bounding box
    if (wasHoveringSelectionBox && params.selectedNodes.length > 1 && hasPermission && !this.state.isShiftPressed) {
      const mapArea = mapAreaService.getState();
      const draggableNodes = params.selectedNodes.filter(node => !node.locked);
      this.multiDragOffsets = draggableNodes.map(node => ({
        nodeId: node.id,
        offsetX: Scale.xInv(x, canvasWidth / 2, zoomLevel) - (node.x + mapArea.x),
        offsetY: Scale.yInv(y, canvasHeight / 2, zoomLevel) - (node.y + mapArea.y)
      }));
      this.multiDragInitialPositions = draggableNodes.map(node => ({
        id: node.id,
        x: node.x,
        y: node.y
      }));
      this.multiDragWasDragged = false;

      // Snapshot positions before drag for undo support
      void saveNodePosition(null);

      this.state.action = PointerAction.DRAG_MULTI_SELECTION;
      return;
    }

    if (this.state.hoveredNodeId !== null && hasPermission) {
      const hoveredNode = nodes.find(n => n.id === this.state.hoveredNodeId);
      if (hoveredNode) {
        const mapArea = mapAreaService.getState();
        this.nodeDX = Scale.xInv(x, canvasWidth / 2, zoomLevel) - (hoveredNode.x + mapArea.x);
        this.nodeDY = Scale.yInv(y, canvasHeight / 2, zoomLevel) - (hoveredNode.y + mapArea.y);

        // Snapshot position before drag for undo support — null case clears the timeout synchronously
        void saveNodePosition(null);

        this.state.action = PointerAction.DRAG_NODE;
        return;
      }
    }

    // Shift+Drag on empty space → lasso selection
    if (this.state.isShiftPressed && (this.state.hoveredNodeId === null || this.state.hoveredNodeId <= -1)) {
      this.state.action = PointerAction.DRAG_SELECTION;
      this.state.selectionBox = { startX: x, startY: y, endX: x, endY: y };
      return;
    }

    // if no node - set action by default
    this.state.action = PointerAction.DRAG_MAP;
  }

  // ---------------------------------------------------------------------------
  // handleUp helper methods
  // ---------------------------------------------------------------------------

  /**
   * Check whether a canvas-space point is inside the multi-selection bounding box.
   *
   * @returns `true` when the cursor is inside the selection AABB
   */
  private isInsideSelectionBox(
    x: number,
    y: number,
    canvasWidth: number,
    canvasHeight: number,
    zoomLevel: number,
    selectedNodes: Node[],
    mapAreaService: HandleMoveParams['mapAreaService']
  ): boolean {
    if (selectedNodes.length < 2) return false;

    const mapArea = mapAreaService.getState();
    const cursorWorldX = Scale.xInv(x, canvasWidth / 2, zoomLevel) - mapArea.x;
    const cursorWorldY = Scale.yInv(y, canvasHeight / 2, zoomLevel) - mapArea.y;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const node of selectedNodes) {
      const border = node.border && node.borderWidth ? node.borderWidth * 2 : 0;
      const halfW = (node.width + border) / 2;
      const halfH = (node.height + border) / 2;

      if (node.x - halfW < minX) minX = node.x - halfW;
      if (node.x + halfW > maxX) maxX = node.x + halfW;
      if (node.y - halfH < minY) minY = node.y - halfH;
      if (node.y + halfH > maxY) maxY = node.y + halfH;
    }

    return cursorWorldX >= minX && cursorWorldX <= maxX &&
           cursorWorldY >= minY && cursorWorldY <= maxY;
  }

  /**
   * Save positions for all multi-dragged nodes and reset multi-drag state.
   *
   * @returns `true` if a multi-drag was completed (caller should return early)
   */
  private async handleMultiDragComplete(params: HandleUpParams): Promise<boolean> {
    if (!this.multiDragWasDragged || this.multiDragOffsets.length === 0) return false;

    const { nodes, mapAreaService, saveNodePosition } = params;

    // Check if any node actually moved
    let anyMoved = false;
    for (const initial of this.multiDragInitialPositions) {
      const node = nodes.find(n => n.id === initial.id);
      if (node && (node.x !== initial.x || node.y !== initial.y)) {
        anyMoved = true;
        break;
      }
    }

    if (anyMoved) {
      for (const initial of this.multiDragInitialPositions) {
        await saveNodePosition(initial.id);
      }

      this.multiDragOffsets = [];
      this.multiDragInitialPositions = [];
      this.multiDragWasDragged = false;
      mapAreaService.updateMap(false);
      this.state.isMouseDown = false;
      mapAreaService.movement = false;
      return true;
    }

    this.multiDragOffsets = [];
    this.multiDragInitialPositions = [];
    this.multiDragWasDragged = false;
    return false;
  }

  /**
   * Save the node's final position after a drag and reset drag state.
   *
   * @returns `true` if a drag was completed (caller should return early)
   */
  private async handleDragComplete(params: HandleUpParams): Promise<boolean> {
    if (!this.nodeWasDragged) return false;

    const { nodes, mapAreaService, saveNodePosition } = params;
    const movedNode = nodes.find(n => n.id === this.mouse.movedNode?.id);

    if (movedNode && this.mouse.movedNode &&
      (movedNode.x !== this.mouse.movedNode.x || movedNode.y !== this.mouse.movedNode.y)) {

      await saveNodePosition(movedNode.id);
      this.state.hoveredNodeId = -1;
      this.mouse.movedNode = null;
      this.nodeWasDragged = false;
      mapAreaService.updateMap(false);

      this.state.isMouseDown = false;
      mapAreaService.movement = false;

      return true;
    }

    // Drag started but node returned to its original position
    this.nodeWasDragged = false;
    this.mouse.movedNode = null;
    return false;
  }

  // ---------------------------------------------------------------------------
  // handleUp — main orchestrator
  // ---------------------------------------------------------------------------

  /**
   * Handle pointer up event — resolves pending drag/resize, toggles
   * node/edge selection, and resets state.
   *
   * @param params - Up interaction parameters
   */
  async handleUp(params: HandleUpParams): Promise<void> {
    const { nodes, mapAreaService, isQuickEditBarContentTracked, updateQuickEditBarContentWarning,
      isNodeDrawingMode, createNode, event, ctx, zoomLevel, mapArea, hasPermission } = params;

    // 1. Sizing completion — delegated to NodeResizeService
    if (await this.nodeResizeService.finalize(params.selectedNode, params.updateNodeDesign, params.saveNodeDesign)) return;

    // 2a. Multi-selection drag completion
    if (await this.handleMultiDragComplete(params)) return;

    // 2a-1. Click inside selection box without dragging — keep selection intact
    if (this.state.action === PointerAction.DRAG_MULTI_SELECTION) {
      this.multiDragOffsets = [];
      this.multiDragInitialPositions = [];
      this.state.isMouseDown = false;
      mapAreaService.movement = false;
      mapAreaService.updateElement(true);
      if (!params.isMapTransfer) {
        mapAreaService.updateMap(false);
      }
      return;
    }

    // 2b. Single drag completion
    if (await this.handleDragComplete(params)) return;

    // 3. Clear hover states
    nodes.forEach(node => { node.hover = false; });

    // 4. QEB tracked changes guard
    if (isQuickEditBarContentTracked) {
      this.state.isMouseDown = false;
      mapAreaService.movement = false;
      await updateQuickEditBarContentWarning(true);
      return;
    }

    // 5. Node creation mode
    if (isNodeDrawingMode && !this.nodeWasDragged &&
      this.state.hoveredNodeId === -1 && this.state.hoveredEdgeId === -1) {
      this.state.isMouseDown = false;
      mapAreaService.movement = false;
      await createNode(event, ctx, zoomLevel, mapArea);
      return;
    }

    // 6. Edge connection mode — delegated to SelectionService
    if (await this.selectionService.handleEdgeConnection(params, this.nodeWasDragged)) return;

    // 7. Permission guard
    if (!hasPermission) {
      this.state.isMouseDown = false;
      return;
    }

    // 8. Lasso selection complete
    if (this.state.action === PointerAction.DRAG_SELECTION && this.state.selectionBox) {
      const selectionBox = { ...this.state.selectionBox };
      this.state.selectionBox = null;
      await this.selectionService.handleLassoSelectionComplete(
        params,
        selectionBox,
        params.ctx,
        params.zoomLevel
      );
      this.state.isMouseDown = false;
      mapAreaService.movement = false;
      mapAreaService.updateElement(true);
      if (!params.isMapTransfer) {
        mapAreaService.updateMap(false);
      }
      return;
    }

    // 9. Node / Edge selection or empty space click — delegated to SelectionService
    const isNodeHovered = this.state.hoveredNodeId !== null && this.state.hoveredNodeId > -1;
    const isEdgeHovered = this.state.hoveredEdgeId !== null && this.state.hoveredEdgeId > -1;
    // Treat tiny drags (< 5px) as clicks so empty-space deselect works reliably
    const dragDistance = Math.hypot(
      this.mouse.x - this.mouse.storedDragX,
      this.mouse.y - this.mouse.storedDragY
    );
    const isClick = dragDistance < 5;

    // Shift OR Ctrl/Cmd extends the selection (Cmd on macOS, Ctrl elsewhere).
    const mouseEvent = params.event as MouseEvent;
    const isExtendModifier =
      params.isShiftPressed || !!(mouseEvent && (mouseEvent.ctrlKey || mouseEvent.metaKey));

    if (isClick && isExtendModifier && isNodeHovered) {
      // Shift/Ctrl/Cmd+Click on node → multi-selection toggle
      await this.selectionService.handleMultiNodeSelection(params);
    } else if (!this.nodeWasDragged && isNodeHovered) {
      await this.selectionService.handleNodeSelection(params);
    } else if (!this.nodeWasDragged && isEdgeHovered) {
      await this.selectionService.handleEdgeSelection(params);
    } else if (isClick || !mapAreaService.movement) {
      await this.selectionService.handleEmptySpaceClick(params);
    }

    // 10. Cleanup
    this.state.hoveredNodeId = -1;
    this.state.hoveredEdgeId = -1;
    this.state.isMouseDown = false;
    mapAreaService.movement = false;
    mapAreaService.updateElement(true);

    if (!params.isMapTransfer) {
      mapAreaService.updateMap(false);
    }
  }

  /**
   * Get current interaction state.
   *
   * @returns The reactive pointer state object
   */
  getState(): PointerState {
    return this.state;
  }

  /**
   * Update element references used for hover detection.
   *
   * @param nodes - Current node array
   * @param edges - Current edge array
   */
  updateElements(nodes: Node[], edges: Edge[]): void {
    this.hoverDetection.updateElements(nodes, edges);
  }
}
